import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  Avatar,
  TextField,
  Pagination,
  Stack,
  CircularProgress,
  IconButton
} from "@mui/material";
import {
  DeleteOutline,
  CloudUploadOutlined,
  Search,
  Close
} from "@mui/icons-material";
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db, storage } from "../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import Swal from "sweetalert2";

const colors = {
  darkGreen: '#06362D',
  calPolyGreen: '#155332',
  vanilla: '#DEE2B1',
  gold: '#FFD500',
  goldenrod: '#D1A505'
};

function Propimages() {
  const auth = getAuth();

  const [userId, setUserId] = useState(null);
  const [propertyId, setPropertyId] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [images, setImages] = useState([]);
  const [roomId, setRoomId] = useState("");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const rowsPerPage = 5;

  /* ================= AUTH ================= */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) return;
      setUserId(user.uid);

      const snap = await getDocs(collection(db, "authroles"));
      snap.forEach((d) => {
        if (d.id === user.uid) {
          setPropertyId(d.data().propertyId);
        }
      });
    });

    return () => unsub();
  }, []);

  /* ================= FETCH ROOMS ================= */
  const fetchRooms = async () => {
    if (!userId || !propertyId) return;

    const snap = await getDocs(collection(db, "rooms"));
    setRooms(
      snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((r) => r.propertyId === propertyId)
    );
  };

  /* ================= FETCH IMAGES ================= */
  const fetchImages = async () => {
    if (!userId || !propertyId) return;

    const snap = await getDocs(collection(db, "property_images"));
    setImages(
      snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((img) => img.propertyId === propertyId)
    );
  };

  useEffect(() => {
    fetchRooms();
    fetchImages();
  }, [userId, propertyId]);

  /* ================= FILE SELECTION ================= */
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles((prev) => [...prev, ...selectedFiles]);
    e.target.value = null;
  };

  const removeSelectedFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  /* ================= UPLOAD ================= */
  const uploadImages = async () => {
    if (!files.length) return;

    setUploading(true);
    try {
      await Promise.all(
        files.map(async (file) => {
          const folder = roomId || "property";
          const storageRef = ref(
            storage,
            `property_images/${propertyId}/${folder}/${Date.now()}_${file.name}`
          );

          await uploadBytes(storageRef, file);
          const url = await getDownloadURL(storageRef);

          await addDoc(collection(db, "property_images"), {
            userId,
            propertyId,
            roomId: roomId || null,
            imageUrl: url,
            createdAt: new Date()
          });
        })
      );

      Swal.fire("Success", "Images uploaded successfully", "success");
      setFiles([]);
      setRoomId("");
      fetchImages();
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    } finally {
      setUploading(false);
    }
  };

  /* ================= DELETE ================= */
  const deleteImage = async (id) => {
    await deleteDoc(doc(db, "property_images", id));
    Swal.fire("Deleted", "Image removed", "success");
    fetchImages();
  };

  /* ================= FILTER ================= */
  const propertyImages = images.filter((img) => !img.roomId);
  const roomImages = images.filter((img) => img.roomId);

  const filteredRoomImages = roomImages.filter((img) =>
    rooms
      .find((r) => r.id === img.roomId)
      ?.roomTypeName?.toLowerCase()
      .includes(search.toLowerCase())
  );

  const paginated = filteredRoomImages.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: colors.darkGreen, color: colors.vanilla, p: { xs: 2, md: 4 } }}>
      <Box component="main" sx={{ flexGrow: 1, maxWidth: "1600px", margin: "0 auto", width: "100%" }}>
        <Typography fontSize={26} fontWeight={800} mb={3} sx={{ color: colors.vanilla }}>
          Property & Room Images
        </Typography>

        {/* ================= UPLOAD ================= */}
        <Card sx={{ borderRadius: 3, mb: 4, border: `1px solid ${colors.goldenrod}`, bgcolor: colors.calPolyGreen, color: colors.vanilla, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.3)" }}>
          <CardContent sx={{ p: 3 }}>
            <Typography fontWeight={700} mb={2} sx={{ color: colors.vanilla }}>
              Upload Images
            </Typography>

            <div className="container-fluid p-0">
              <div className="row align-items-center">
                <div className="col-sm-4 mb-3">
                  <Select
                    fullWidth
                    size="small"
                    value={roomId}
                    onChange={(e) => setRoomId(e.target.value)}
                    displayEmpty
                    disabled={uploading}
                    sx={{ 
                      color: colors.vanilla, 
                      backgroundColor: colors.darkGreen,
                      borderRadius: "8px",
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.goldenrod }, 
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.gold }, 
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.gold },
                      '& .MuiSvgIcon-root': { color: colors.vanilla }
                    }}
                  >
                    <MenuItem value="">Property Images</MenuItem>
                    {rooms.map((r) => (
                      <MenuItem key={r.id} value={r.id}>
                        {r.roomTypeName}
                      </MenuItem>
                    ))}
                  </Select>
                </div>

                <div className="col-sm-8 mb-3">
                  <input
                    type="file"
                    multiple
                    hidden
                    id="uploadInput"
                    accept="image/*"
                    onChange={handleFileChange}
                  />

                  <Button
                    variant="outlined"
                    startIcon={<CloudUploadOutlined />}
                    onClick={() =>
                      document.getElementById("uploadInput").click()
                    }
                    disabled={uploading}
                    sx={{
                      borderColor: colors.goldenrod,
                      color: colors.vanilla,
                      borderRadius: "8px",
                      textTransform: "none",
                      '&:hover': { borderColor: colors.gold, bgcolor: 'rgba(255,255,255,0.05)' }
                    }}
                  >
                    Select Images
                  </Button>

                  <Button
                    sx={{ 
                      ml: 2, 
                      minWidth: 120,
                      bgcolor: colors.goldenrod, 
                      color: colors.darkGreen, 
                      fontWeight: 700, 
                      textTransform: "none", 
                      borderRadius: "8px",
                      '&:hover': { bgcolor: colors.gold } 
                    }}
                    variant="contained"
                    disabled={!files.length || uploading}
                    onClick={uploadImages}
                    startIcon={
                      uploading ? <CircularProgress size={20} color="inherit" /> : null
                    }
                  >
                    {uploading ? "Uploading..." : "Upload"}
                  </Button>
                </div>
              </div>
            </div>

            {/* ================= SELECTED FILES PREVIEW ================= */}
            {files.length > 0 && (
              <Box mt={3}>
                <Typography variant="subtitle2" fontWeight={600} mb={1} sx={{ color: colors.vanilla }}>
                  Selected Images ({files.length}):
                </Typography>
                <Grid container spacing={2}>
                  {files.map((file, index) => {
                    const previewUrl = URL.createObjectURL(file);
                    return (
                      <Grid item key={index}>
                        <Box
                          sx={{
                            position: "relative",
                            width: 100,
                            height: 80,
                            borderRadius: 2,
                            overflow: "hidden",
                            border: `1px solid ${colors.goldenrod}`
                          }}
                        >
                          <Avatar
                            variant="rounded"
                            src={previewUrl}
                            sx={{ width: "100%", height: "100%" }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => removeSelectedFile(index)}
                            disabled={uploading}
                            sx={{
                              position: "absolute",
                              top: 2,
                              right: 2,
                              bgcolor: "rgba(0, 0, 0, 0.6)",
                              color: "#fff",
                              p: "2px",
                              "&:hover": { bgcolor: "rgba(0, 0, 0, 0.8)" }
                            }}
                          >
                            <Close sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>
                        <Typography
                          variant="caption"
                          noWrap
                          sx={{ display: "block", width: 100, mt: 0.5, color: colors.vanilla, opacity: 0.8 }}
                        >
                          {file.name}
                        </Typography>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* ================= PROPERTY IMAGES ================= */}
        <Card sx={{ borderRadius: 3, mb: 4, border: `1px solid ${colors.goldenrod}`, bgcolor: colors.calPolyGreen, color: colors.vanilla, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.3)" }}>
          <CardContent sx={{ p: 3 }}>
            <Typography fontWeight={700} mb={2} sx={{ color: colors.vanilla }}>
              Property Images
            </Typography>

            <Grid container spacing={2}>
              {propertyImages.map((img) => (
                <Grid item key={img.id}>
                  <Avatar
                    variant="rounded"
                    src={img.imageUrl}
                    sx={{ width: 160, height: 100, border: `1px solid ${colors.goldenrod}` }}
                  />
                  <Button
                    color="error"
                    size="small"
                    startIcon={<DeleteOutline />}
                    onClick={() => deleteImage(img.id)}
                    sx={{ mt: 1 }}
                  >
                    Delete
                  </Button>
                </Grid>
              ))}
              {propertyImages.length === 0 && (
                <Typography variant="body2" sx={{ color: colors.vanilla, opacity: 0.7, p: 2 }}>
                  No property images uploaded yet.
                </Typography>
              )}
            </Grid>
          </CardContent>
        </Card>

        {/* ================= ROOM TYPE IMAGES ================= */}
        <Card sx={{ borderRadius: 3, border: `1px solid ${colors.goldenrod}`, bgcolor: colors.calPolyGreen, color: colors.vanilla, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.3)" }}>
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography fontWeight={700} sx={{ color: colors.vanilla }}>
                Room Type Images
              </Typography>

              <TextField
                size="small"
                placeholder="Search room"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputLabelProps={{ style: { color: colors.vanilla } }}
                InputProps={{
                  style: { color: colors.vanilla },
                  startAdornment: <Search sx={{ mr: 1, color: colors.goldenrod }} />
                }}
                sx={{
                  width: 250,
                  "& .MuiOutlinedInput-root": { 
                    borderRadius: "8px",
                    backgroundColor: colors.darkGreen,
                    "& fieldset": { borderColor: colors.goldenrod },
                    "&:hover fieldset": { borderColor: colors.gold },
                    "&.Mui-focused fieldset": { borderColor: colors.gold }
                  } 
                }}
              />
            </Stack>

            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "8px", border: `1px solid ${colors.goldenrod}`, backgroundColor: "transparent" }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: colors.darkGreen }}>
                  <TableRow>
                    <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Image</TableCell>
                    <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Room Type</TableCell>
                    <TableCell align="right" sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Action</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {paginated.map((img) => (
                    <TableRow key={img.id} hover sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}>
                      <TableCell sx={{ borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                        <Avatar
                          variant="rounded"
                          src={img.imageUrl}
                          sx={{ width: 90, height: 60, border: `1px solid ${colors.goldenrod}` }}
                        />
                      </TableCell>

                      <TableCell sx={{ color: colors.gold, fontWeight: 600, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                        {rooms.find((r) => r.id === img.roomId)?.roomTypeName}
                      </TableCell>

                      <TableCell align="right" sx={{ borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                        <Button
                          color="error"
                          size="small"
                          startIcon={<DeleteOutline />}
                          onClick={() => deleteImage(img.id)}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}

                  {paginated.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 4, color: colors.vanilla, opacity: 0.7 }}>
                        No room type images found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box display="flex" justifyContent="flex-end" mt={3}>
              <Pagination
                count={Math.ceil(filteredRoomImages.length / rowsPerPage) || 1}
                page={page}
                onChange={(e, v) => setPage(v)}
                color="primary"
                size="small"
              />
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

export default Propimages;